import React, { useEffect, useState, useCallback } from 'react';
import { Card, Form, Input, Button, Select, DatePicker, message, Row, Col, InputNumber, Table, Space, Modal, Popconfirm } from 'antd';
import { CopyOutlined, SendOutlined, PlusOutlined, EditOutlined, EyeOutlined, DeleteOutlined, SearchOutlined, FileTextOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import './index.css';

const { Option } = Select;
const { TextArea } = Input;

const OrderPlace = () => {
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('add'); // add | edit | view
  const [currentRecord, setCurrentRecord] = useState(null);

  const [dataSource, setDataSource] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const [shops, setShops] = useState([]);


  // 列定义（与模型字段）
  const columns = [
    { title: '名称', dataIndex: 'name', key: 'name', width: 140 },
    { title: '日期', dataIndex: 'date', key: 'date', width: 120, render: (v) => (v ? v.slice(0, 10).replace(/-/g, '/') : '-') },
    { title: '微信号', dataIndex: 'wechat_id', key: 'wechat_id', width: 160 },
    { title: '会员等级', dataIndex: 'member_level', key: 'member_level', width: 120 },
    { title: '所属店铺', dataIndex: 'shop_id', key: 'shop_id', width: 160, render: (id) => shops.find(s => s.value === id)?.label || id || '-' },
    { title: '用户编号', dataIndex: 'user_id', key: 'user_id', width: 140 },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 240, // 增加宽度以容纳新按钮
      render: (_, record) => (
        <Space size="small">
          <Button type="primary" icon={<CopyOutlined />} onClick={() => handleCopy(record)} size="small"></Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small">编辑</Button>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handleView(record)} size="small">查看</Button>
          <Popconfirm
            title="确定要删除这条记录吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small">删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 列表加载
  const loadData = useCallback(async (page = pagination.current, pageSize = pagination.pageSize, searchOverride = searchText) => {
    try {
      setLoading(true);
      const res = await axios.get('/api/place-orders', {
        params: {
          page,
          per_page: pageSize,
          search: (searchOverride && searchOverride.trim()) || undefined
        }
      });
      if (res.data?.success) {
        setDataSource(res.data.data);
        setPagination(prev => ({ ...prev, current: page, pageSize, total: res.data.total }));
      } else {
        throw new Error(res.data?.message || '加载失败');
      }
    } catch (e) {
      console.error(e);
      message.error(e.message || '加载失败');
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize]);



  useEffect(() => {
    loadData();

  }, [loadData]);

  const handleTableChange = async (p) => {
    await loadData(p.current, p.pageSize, searchText);
  };

  // 创建
  const handleAdd = () => {
    setModalType('add');
    setCurrentRecord(null);
    modalForm.resetFields();
    setModalVisible(true);
  };

  // 编辑
  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentRecord(record);
    modalForm.setFieldsValue({
      name: record.name,
      date: record.date ? dayjs(record.date) : null,
      wechat_id: record.wechat_id,
      member_level: record.member_level,
      shop_id: record.shop_id,
      user_id: record.user_id,
      address: record.address,
      deliveryMethod: record.delivery_method,
      deliveryTime: record.delivery_time ? dayjs(record.delivery_time) : null,
      remarks: record.remarks
    });
    setModalVisible(true);
  };

  // 查看
  const handleView = (record) => {
    setModalType('view');
    setCurrentRecord(record);
    modalForm.setFieldsValue({
      name: record.name,
      date: record.date ? dayjs(record.date) : null,
      wechat_id: record.wechat_id,
      member_level: record.member_level,
      shop_id: record.shop_id,
      user_id: record.user_id,
      address: record.address,
      deliveryMethod: record.delivery_method,
      deliveryTime: record.delivery_time ? dayjs(record.delivery_time) : null,
      remarks: record.remarks
    });
    setModalVisible(true);
  };

  // 删除
  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(`/api/place-orders/${id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` }
      });
      if (!res.data?.success) throw new Error(res.data?.message || '删除失败');
      message.success('删除成功');
      await loadData();
    } catch (e) {
      console.error(e);
      message.error(e.message || '删除失败');
    }
  };

  // 提交（新增/更新）
  const handleSubmit = async () => {
    try {
      const values = await modalForm.validateFields();
      const payload = {
        name: values.name,
        date: values.date ? values.date.format('YYYY-MM-DD') : null,
        wechat_id: values.wechat_id || null,
        member_level: values.member_level || null,
        shop_id: values.shop_id ?? null,
        user_id: values.user_id
      };

      const isEdit = modalType === 'edit' && currentRecord?.id;
      const url = isEdit ? `/api/place-orders/${currentRecord.id}` : '/api/place-orders';
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || (isEdit ? '更新失败' : '创建失败'));

      message.success(isEdit ? '更新成功' : '创建成功');
      setModalVisible(false);
      modalForm.resetFields();
      await loadData();
    } catch (e) {
      console.error(e);
      if (e?.errorFields) return; // 表单校验错误
      message.error(e.message || '提交失败');
    }
  };



  // 获取商店
  const loadShops = useCallback(async () => {
    try {
      const res = await axios.get('/api/storesoptions');
      if (res.data && Array.isArray(res.data)) {
        setShops(res.data);
      }
    } catch (error) {
      console.error('加载店铺列表失败:', error);
      message.error('加载店铺列表失败');
    }
  }, []);

  useEffect(() => {
    loadData();
    loadShops(); // 添加这行
  }, [loadData, loadShops]); // 添加loadShops依赖


  // 获取下一个用户编号
  const getNextUserId = useCallback(async (memberLevel) => {
    try {
      const res = await axios.get('/api/place-orders/next-user-id', {
        params: { member_level: memberLevel }
      });
      if (res.data?.success) {
        return res.data.data.next_user_id;
      }
    } catch (error) {
      console.error('获取下一个用户编号失败:', error);
      message.error('获取下一个用户编号失败');
    }
    return null;
  }, []);

  // 处理会员等级变化
  const handleMemberLevelChange = async (value) => {
    if (value && (value === 'VIP会员用户' || value === '普通用户')) {
      const nextUserId = await getNextUserId(value);
      if (nextUserId) {
        modalForm.setFieldsValue({ user_id: nextUserId });
      }
    }
  };


  // 在组件中添加复制函数
const handleCopy = (record) => {
  const shopName = shops.find(s => s.value === record.shop_id)?.label || record.shop_id || '';
  const copyText = `${shopName}-${record.name}-${record.user_id}`;
  
  // 首先尝试使用现代API
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(copyText).then(() => {
      message.success('复制成功');
    }).catch(() => {
      // 如果现代API失败，使用降级方案
      fallbackCopyText(copyText);
    });
  } else {
    // 如果不支持现代API，直接使用降级方案
    fallbackCopyText(copyText);
  }
};

// 降级处理函数
const fallbackCopyText = (text) => {
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    // 避免滚动到底部
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      message.success('复制成功');
    } else {
      message.error('复制失败');
    }
  } catch (err) {
    message.error('复制失败');
    console.error('复制操作失败:', err);
  }
};

  return (
    <div className="order-place-container">
      <Card
        title={
          <div className="order-place-page-header">
            <FileTextOutlined className="page-icon" />
            <span>下单填写</span>
          </div>
        }
        className="page-card"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            创建
          </Button>
        }
      >

        {/* 搜索框（预留占位，当前仅样式） */}
        <div className="search-container" style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="搜索订单信息..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onClear={() => {
              setSearchText('');
              setPagination(prev => ({ ...prev, current: 1 }));
              loadData(1, pagination.pageSize, '');
            }}
            onSearch={() => loadData(1, pagination.pageSize, searchText)}
            style={{ width: 400 }}
          />
        </div>

        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `共${total} 条记录, 第${range[0]}-${range[1]}条`,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      </Card>

      <Modal
        title={modalType === 'view' ? '查看下单' : (modalType === 'edit' ? '编辑下单' : '创建下单')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width="70%"
        footer={
          modalType === 'view'
            ? [<Button key="close" onClick={() => setModalVisible(false)}>关闭</Button>]
            : [
              <Button key="cancel" onClick={() => setModalVisible(false)}>取消</Button>,
              <Button key="save" type="primary" icon={<SendOutlined />} onClick={handleSubmit}>保存</Button>
            ]
        }
      >
        <Form
          form={modalForm}
          layout="vertical"
          disabled={modalType === 'view'}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="名称" name="name" rules={[{ required: true, message: '请输入名称' }]}>
                <Input placeholder="请输入名称" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="日期" name="date" rules={[{ required: true, message: '请选择日期' }]}>
                <DatePicker style={{ width: '100%' }} placeholder="年/月/日" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="微信号" name="wechat_id">
                <Input placeholder="请输入微信号" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="会员等级" name="member_level">
                <Select
                  placeholder="请选择会员等级"
                  allowClear
                  disabled={modalType === 'view' || modalType === 'edit'} // 更新时禁用
                  onChange={handleMemberLevelChange}
                >
                  <Option value="普通用户">普通用户</Option>
                  <Option value="VIP会员用户">VIP会员用户</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
            <Form.Item label="所属店铺" name="shop_id">
              <Select 
                placeholder="请选择店铺" 
                allowClear 
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {shops.map(s => (
                  <Option key={s.value} value={s.value}>{s.label}</Option>
                ))}
              </Select>
            </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="用户编号" name="user_id" rules={[{ required: true, message: '请输入用户编号' }]}>
                <Input
                  placeholder="请输入用户编号"
                  disabled={modalType === 'view' || modalType === 'edit'} // 更新时禁用
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="备注" name="remarks">
            <TextArea rows={3} placeholder="请输入订单备注" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OrderPlace;